import {type RefObject, useLayoutEffect} from 'react'

/**
 * Put a scroll container back to the start on both axes, now, with no animation.
 *
 * `scrollBehavior` is set to `auto` around the assignment rather than passing
 * `{behavior: 'instant'}` to `scrollTo`, because Safari does not support that option
 * and would animate instead. A reset that animates is worse than one that does not:
 * the data underneath has already changed, so the board would scroll through content
 * that no longer means anything.
 */
export function resetScrollPositionImmediately(element: HTMLElement | null | undefined): void {
  if (!element) return

  const previousBehavior = element.style.scrollBehavior
  element.style.scrollBehavior = 'auto'
  element.scrollTop = 0
  element.scrollLeft = 0
  element.style.scrollBehavior = previousBehavior
}

/**
 * Reset `scrollRef` whenever `resetScrollKey` changes.
 *
 * The board does not restore scroll position, because only the caller knows whether the
 * data it just supplied is the same board scrolled further or a different board
 * altogether. It states which by changing this key, and the board starts from the
 * beginning when it does.
 *
 * Runs in a layout effect so the reset lands in the same frame as the render that
 * changed the key, rather than showing one frame of the new data at the old offset.
 */
export function useResetScroll(scrollRef: RefObject<HTMLElement | null>, resetScrollKey: string | number | undefined) {
  useLayoutEffect(() => {
    if (resetScrollKey === undefined) return

    resetScrollPositionImmediately(scrollRef.current)
  }, [resetScrollKey, scrollRef])
}
